import '../../styles/Forms.scss'
import {ServiceEnum} from '../../../model/ServiceEnum'
import {useCurrentGuildContext, useDatxPathGenerator} from '../../../../util/routing'
import {useCreateNewManufacturerItemServiceRequestMutation} from '../../rtkServiceRequestApi'
import {ROUTES} from '../../../../constants/routing'
import {
    EndOfLifeStatusType,
    EnvironmentalRegulationType,
    EnvironmentalRegulationTypes,
    EvidenceDocumentsType,
    ExportControlClassificationType,
    ExportControlClassificationTypes,
    ItemCreationForm,
    ItemMatchingInputDTO,
    NewItemInputDTO,
    ServiceFields
} from '../../../model/NewItemInput'
import {array, boolean, mixed, object, ObjectSchema, string} from 'yup'
import {ServiceRequestFormTemplate} from '../../genericComponents/formTemplate/ServiceRequestFormTemplate'
import {ServiceRequestStep} from '../../model/ServiceRequestStep'
import {ServiceRequestWorkflowInput} from '../../model/ServiceRequestWorkflowInput'
import {CompleteManufacturerItemCreationForm} from './CreationStep/CompleteManufacturerItemCreationForm'
import './NewManufacturerItemsForm.scss'
import LoadingSpinner from '../../../../genericComponents/LoadingSpinner'
import {
    toCommentsAttributes,
    toEndOfLifeStatusAttributes,
    toEnvironmentalRegulationAttributes,
    toEvidenceDocumentsAttributes,
    toExportControlClassificationAttributes
} from '../../../model/attribute/AttributeModelMapper'
import {useEffect, useState} from 'react'
import {ClassificationNode, FileDocument} from '../../../../displaySet/model/AttributeValue'
import {ItemOrganization} from '../../../../item/model/ItemOrganization'
import {NewManufacturerItemsConfirmation} from './NewManufacturerItemsConfirmation'
import {AttributeModel} from '../../../../displaySet/model/AttributeModel'
import {useGetAccessibleWorkflowsQuery} from '../../../../guild/rtkGuildApi'
import {WorkflowConfiguration} from '../../../../guild/workflowConfiguration/model/WorkflowConfiguration'

export interface ManufacturerItemCreationForm extends ServiceRequestWorkflowInput {
    file: File | undefined
    items: NewItemInputDTO[] | ItemMatchingInputDTO[]
}

const fileDocumentSchema : ObjectSchema<FileDocument> = object({
    file: mixed(),
    externalUrl : string().url('URL should be valid'),
    isFile : boolean(),
    localFile: mixed()
}).test((value, context) => value.externalUrl || value.file)

const proofSchema : ObjectSchema<EvidenceDocumentsType> = object({
    proofs: array().of(fileDocumentSchema.defined()).default([])
})

const evidenceDocumentSchema : ObjectSchema<EvidenceDocumentsType> = proofSchema.test((value, context) => value.proofs.length >= 1)

const exportControlSchema : ObjectSchema<ExportControlClassificationType> = object({
    type : string().oneOf(Object.values(ExportControlClassificationTypes))
}).concat(proofSchema)

const environmentalRegulationSchema : ObjectSchema<EnvironmentalRegulationType> = object({
    type : string().oneOf(Object.values(EnvironmentalRegulationTypes))
}).concat(proofSchema)

const endOfLifeSchema : ObjectSchema<EndOfLifeStatusType> = object({
    date: string()
}).concat(proofSchema)

const staticSchema : ObjectSchema<Omit<ItemCreationForm, 'organizationInput'>> = object({
    partNumber: string().required('Part Number is required').default(''),
    definedByOrganization: mixed<ItemOrganization>().required(),
    technicalClassificationNode: mixed<ClassificationNode>().required(),
    [ServiceFields.EvidenceDocuments]: evidenceDocumentSchema,
    [ServiceFields.EndOfLifeStatus]:  endOfLifeSchema,
    [ServiceFields.ExportControlClassification]:  exportControlSchema,
    [ServiceFields.EnvironmentalRegulation]:  array().of(environmentalRegulationSchema.defined()).default([]),
    [ServiceFields.Comments]: string().default('')
})


const defaultValues = staticSchema.getDefault()

export const NewManufacturerItemsForm = () => {
    const generatePath = useDatxPathGenerator()
    const [currentGuildContext] = useCurrentGuildContext()
    const [createRequest] = useCreateNewManufacturerItemServiceRequestMutation()
    
    const {data: workflows, isLoading} = useGetAccessibleWorkflowsQuery({
        guildId: currentGuildContext?.guildId,
        service: ServiceEnum.NEW_MANUFACTURER_ITEMS
    })

    const [defaultWorkflow, setDefaultWorkflow] = useState<WorkflowConfiguration>()

    const [submittedValues, setSubmittedValues] = useState<{
        serviceRequestId: string,
        serviceDetails: ItemCreationForm
    }>()

    useEffect(() => {
        if (workflows && workflows.length > 0) {
            setDefaultWorkflow(workflows.find(workflow => workflow.isDefault))
        }
    }, [workflows])

    if (isLoading) {
        return <LoadingSpinner/>
    }

    const handleCreateItem = (itemCreation: ItemCreationForm): Promise<any> | undefined => {
        if (!itemCreation.partNumber) return
        if (!itemCreation.definedByOrganization) return
        if (!itemCreation.technicalClassificationNode) return
        const evidenceDocumentAttributes = toEvidenceDocumentsAttributes(itemCreation.EvidenceDocuments)
        const endOfLifeStatusAttributes = toEndOfLifeStatusAttributes(itemCreation.EndOfLifeStatus)
        const exportControlClassificationAttributes = toExportControlClassificationAttributes(itemCreation.ExportControlClassification)
        const environmentalRegulationAttributes = toEnvironmentalRegulationAttributes(itemCreation.EnvironmentalRegulation)
        const commentsAttributes = toCommentsAttributes(itemCreation.Comments)

        const attributes = [
            ...evidenceDocumentAttributes,
            ...endOfLifeStatusAttributes,
            ...exportControlClassificationAttributes,
            ...environmentalRegulationAttributes,
            ...commentsAttributes]

        const getFiles = (attributes: AttributeModel[]): File[] => {
            const files: File[] = []
            attributes.forEach(attribute => {
                const file = attribute.value.fileDocument?.localFile
                if (file) {
                    files.push(file)
                }
            })
            return files
        }
        return createRequest({
            service: ServiceEnum.NEW_MANUFACTURER_ITEMS,
            guildId: currentGuildContext?.guildId ?? '',
            partNumber: itemCreation.partNumber,
            definedByOrganizationId: itemCreation.definedByOrganization.id,
            technicalClassificationNodeId: itemCreation.technicalClassificationNode.id,
            workflowConfigurationId: defaultWorkflow?.id || '',
            attributes: attributes,
            files: getFiles(attributes)
        }).then(result => {
                if (!result.error)
                    setSubmittedValues({
                    serviceRequestId: result.data?.id,
                    serviceDetails: itemCreation
                })
                return result
            }
        )
    }

    const steps: ServiceRequestStep[] = [
        {
            name: 'Request New Item',
            component: <CompleteManufacturerItemCreationForm/>,
            pageValidation: staticSchema
        }
    ]

    return <ServiceRequestFormTemplate
        className="new-manufacturer-items-form"
        steps={steps}
        handleFormSubmit={handleCreateItem}
        initialValues={defaultValues}
        title="Request New Item"
        previousUrl={generatePath(ROUTES.servicesCatalogue.path)}
        optionalConfirmationStep={<NewManufacturerItemsConfirmation serviceDetails={submittedValues?.serviceDetails}
                                                                    requestId={submittedValues?.serviceRequestId}/>}
    />
}
